import React from 'react';
import PropTypes from 'prop-types';
import ReactFileReader from 'react-file-reader';
import { remove } from 'lodash';
import moment from 'moment';
import CustomDatePicker from './CustomDatePicker';
import CheckboxWrapper from '../Wrappers/CheckboxWrapper';
import InputWrapper from '../Wrappers/InputWrapper';
import SelectWrapper from '../Wrappers/SelectWrapper';
import TextAreaWrapper from '../Wrappers/TextAreaWrapper';
import styled from 'styled-components';

import 'react-datepicker/dist/react-datepicker.css';

const StepWrapper = styled.section`
  display: flex;
  flex: 0 0 auto;
  margin: 0 0 15px 0;
`;

const StepNumbers = styled.div`
  display: flex;
  flex-direction: column;
  padding-right: 15px;
`;

const StepNumber = styled.div`
  color: #d23823;
  font-size: 2rem;
  line-height: 1;
`;

const TotalSteps = styled.div`
  font-size: 0.9rem;
`;

const Divider = styled.hr`
  border-top: 1px solid #ccc;
`;

const FinePrint = styled.div`
  font-size: 0.8rem;
  line-height: 1.2;
  margin-bottom: 1rem;
`;

const Step = props => {
  return (
    <StepWrapper>
      <StepNumbers>
        <StepNumber>{props.step}</StepNumber>
        <TotalSteps>of 09</TotalSteps>
      </StepNumbers>
      <div className="w-100">
        {props.children}
        <Divider />
      </div>
    </StepWrapper>
  );
};

const Header = styled.div`
  margin: 3px 0 0.8rem 0;
`;

const Title = styled.h1`
  font-size: 0.9rem;
  margin-bottom: 0.2rem;
`;

const RestrictionCheckbox = props => {
  return (
    <label style={{ margin: '0 10px 10px 0' }}>
      <input
        type="checkbox"
        checked={props.checked}
        onChange={props.onChange}
        style={{ marginRight: '5px' }}
        readOnly={props.readOnly}
      />
      {props.label}
    </label>
  );
};

const PercentageInputWrapper = styled.div`
  border: 1px solid #484848;
  display: inline-block;
  padding-right: 1rem;
  position: relative;

  &:after {
    content: '%';
    display: inline-block;
    position: absolute;
    right: 8px;
    top: 8px;
  }
`;

const RoundButton = styled.button.attrs({
  disabled: props => props.disabled
})`
  align-items: center;
  background: #fff;
  border-radius: 999em;
  border: 2px solid #421e2c;
  color: #421e2c;
  display: flex-inline;
  font-size: 1.6rem;
  height: 50px;
  line-height: 1;
  padding: 0;
  width: 50px;

  &:hover {
    background: #eee;
  }

  &:focus {
    background: #eee;
    outline: none;
  }

  ${props =>
    props.disabled &&
    `
    border: 2px solid #ccc;
    color: #ccc;

    &:hover {
      background: transparent;
    }
  `};
`;

const Textarea = styled.textarea`
  border: 1px solid #ccc !important;
  min-height: 4rem;
  width: 100%;
`;

export default class DealPost extends React.Component {
  minDeals = 50;

  state = {
    terms: false,
    values: [5, 10, 15, 20, 25, 30, 35, 40, 50],
    restrictions: [
      'None',
      'One per table, per visit',
      'Weekdays only (Mon-Thu)',
      'Not valid on alcohol unless purchased with food',
      'Must be 21+',
      'Breakfast only',
      'Brunch only',
      'Lunch only',
      'Dinner only',
      'Dine-in only',
      'Happy hour only',
      'Not valid for Maine Restaurant Week'
    ],
    maxPurchases: 1,
    maxDeals: this.minDeals,
    promoLength: 1,
    donation: false,
    donationValue: 0,
    donationErrorMessage: null,
    customCodes: false
  };

  componentDidMount() {
    this.props.field('body').validations.val = 'deal';
  }

  dealsSoldBlur = () => {
    const { field, onChange } = this.props;
    const val = Math.round(
      field('dealsSold')
        .validations.val.toString()
        .replace(/[^\d.]+/g, '')
    );

    onChange(field('dealsSold'), val < 50 ? 50 : val);
    this.props.valid(field('dealsSold'));
  };

  donationBlur = () => {
    const { field, onChange } = this.props;

    if (!field('donation').validations.val) {
      this.setState({
        donationValue: 0
      });
      return null;
    }

    if (parseInt(field('donation').validations.val, 10) < 1) {
      onChange(field('donation'), 1);

      this.setState({
        donationValue: field('donation').validations.val,
        donationErrorMessage: 'Minimum donation is $1'
      });
    } else {
      if (parseInt(field('donation').validations.val, 10) > 100) {
        onChange(field('donation'), 100);
      }
      if (typeof field('donation').validations.val === 'string') {
        onChange(
          field('donation'),
          parseInt(field('donation').validations.val, 10)
        );
      }

      this.setState({
        donationValue: field('donation').validations.val,
        donationErrorMessage: null
      });
    }
  };

  toggleRestriction(value) {
    const { field } = this.props;
    const restrictions = field('restrictions');

    if (restrictions.validations.val.indexOf(value) >= 0) {
      remove(restrictions.validations.val, r => r === value);
    } else {
      remove(restrictions.validations.val, r => r === 'None');
      restrictions.validations.val.push(value);
    }

    if (value === 'None' && restrictions.validations.val.indexOf(value) >= 0) {
      restrictions.validations.val = [value];
    }

    return this.props.onChange(restrictions, restrictions.validations.val);
  }

  increase = input => {
    this.props.onChange(input, input.validations.val + 1);
  };

  decrease = input => {
    this.props.onChange(
      input,
      input.validations.val <= 1 ? 1 : input.validations.val - 1
    );
  };

  handleStartChange = date => {
    const { field, onChange } = this.props;

    onChange(field('start'), date);
    onChange(field('end'), date.clone().add(14, 'days'));
  };

  handleEndChange = date => {
    const { field, onChange } = this.props;
    onChange(field('end'), date);
  };

  handleTermsChange = e => {
    this.setState({ terms: e.target.checked });
  };

  handleDonationCheckboxChange = e => {
    this.setState({ donation: e.target.checked });
  };

  toggleCustomCodes = e => {
    this.setState({ customCodes: e.target.checked });
  };

  render() {
    const {
      croppedImage,
      field,
      handleImage,
      removeImage,
      social
    } = this.props;

    const { values, restrictions } = this.state;

    if (!field('dealValue')) return null;

    return (
      <div className="SpecialPost">
        <div className="mb-4 text-tomato">
          All fields required unless marked as optional
        </div>
        <Step step="01">
          <Header>
            <Title>DEAL VALUE</Title>
          </Header>

          <SelectWrapper
            {...this.props}
            editing={true}
            input={field('dealValue')}
            style={{
              wrapper: {
                width: '180px'
              }
            }}
            list={values.map(v => ({
              label:
                '$' +
                v +
                ' for $' +
                v / social.get('deals').get('defaultDivisor'),
              value: v
            }))}
          />
          <div style={{ marginTop: '15px' }}>
            <label>
              <input
                type="checkbox"
                onChange={this.handleDonationCheckboxChange}
              />
              <Title style={{ display: 'inline', marginLeft: '.25rem' }}>
                ADD A CHARITABLE DONATION
              </Title>
            </label>
            {this.state.donation && (
              <div>
                <FinePrint>
                  Designate a percentage of the paid value to go to the charity
                  of your choice
                </FinePrint>
                <div className="d-flex flex-wrap">
                  <label style={{ marginRight: '30px' }}>
                    Donation Amount:<br />
                    <PercentageInputWrapper>
                      <InputWrapper
                        {...this.props}
                        input={field('donation')}
                        onBlur={this.donationBlur}
                        blurValidate={false}
                        type="text"
                        style={{
                          input: {
                            border: '0',
                            padding: '.25rem .5rem',
                            minHeight: '42px',
                            width: '50px',
                            textAlign: 'right'
                          }
                        }}
                      />
                    </PercentageInputWrapper>
                    {this.state.donationValue > 0 && (
                      <span className="ml-2">
                        (${(
                          Math.round(
                            this.state.donationValue *
                              field('dealValue').validations.val
                          ) / 100
                        ).toFixed(2)}{' '}
                        / deal)
                      </span>
                    )}
                    {this.state.donationErrorMessage && (
                      <div style={{ color: '#d23823' }}>
                        <small>{this.state.donationErrorMessage}</small>
                      </div>
                    )}
                  </label>

                  <label>
                    Donation Recipient:
                    <InputWrapper
                      {...this.props}
                      input={field('recipient')}
                      blurValidate={false}
                      type="text"
                      style={{
                        input: {
                          border: '1px solid #484848',
                          padding: '.25rem .5rem',
                          minHeight: '44px',
                          width: '200px'
                        }
                      }}
                    />
                  </label>
                </div>
              </div>
            )}
          </div>
        </Step>
        <Step step="02">
          <Header>
            <Title>RESTRICTIONS</Title>
          </Header>
          {restrictions.map((r, i) => (
            <RestrictionCheckbox
              key={i}
              checked={field('restrictions').validations.val.includes(r)}
              onChange={() => this.toggleRestriction(r)}
              label={r}
            />
          ))}
          <label className="mt-3" htmlFor="customRestriction">
            Custom Restrictions (optional, comma delimited):
          </label>
          <InputWrapper
            id="CustomRestriction"
            {...this.props}
            input={field('customRestrictions')}
            blurValidate={false}
            type="text"
            style={{
              input: {
                border: '1px solid #484848',
                padding: '.25rem .5rem',
                minHeight: '44px',
                width: '100%'
              }
            }}
          />
          <div>
            <small>
              <i className="fa fa-info-circle" aria-hidden="true" /> Our data
              shows that offers with fewer restrictions perform better
            </small>
          </div>
        </Step>
        <Step step="03">
          <Header>
            <Title>MAX. PURCHASES PER CUSTOMER</Title>
          </Header>
          <RoundButton onClick={this.decrease.bind(this, field('purchases'))}>
            <i className="fa fa-minus" aria-hidden="true" />
            <span className="sr-only">Decrease</span>
          </RoundButton>
          <span style={{ fontSize: '1.6rem', margin: '0 10px' }}>
            {field('purchases').validations.val}
          </span>
          <RoundButton onClick={this.increase.bind(this, field('purchases'))}>
            <i className="fa fa-plus" aria-hidden="true" />
            <span className="sr-only">Increase</span>
          </RoundButton>
        </Step>
        <Step step="04">
          <Header>
            <Title>NUMBER OF DEALS TO BE SOLD (MIN. {this.minDeals})</Title>
          </Header>

          {/* input type is text instead of number to work around handling
          commas in the input */}
          <InputWrapper
            {...this.props}
            input={field('dealsSold')}
            onBlur={this.dealsSoldBlur}
            type="text"
            style={{
              input: {
                border: '1px solid #484848',
                padding: '.25rem .5rem',
                minHeight: '44px',
                width: '100px'
              }
            }}
          />
        </Step>
        <Step step="05">
          <Header>
            <Title>BEGINNING / END DATE OF DEAL</Title>
            <FinePrint>
              These are the days your deal will be available for purchase on
              Localvore
            </FinePrint>
          </Header>
          <div className="d-inline-block mr-4 mb-2">
            <CustomDatePicker
              minDate={moment()}
              selected={field('start').validations.val}
              onChange={this.handleStartChange}
            />
          </div>

          <span className="mr-4 mb-2">until</span>
          <div className="d-inline-block mr-4 mb-2">
            <CustomDatePicker
              selected={field('end').validations.val}
              minDate={field('start')
                .validations.val.clone()
                .add(14, 'days')}
              onChange={this.handleEndChange}
            />
          </div>
        </Step>
        <Step step="06">
          <Header>
            <Title>LENGTH OF PROMOTIONAL VALUE</Title>
            <FinePrint>
              This is how long your customers will be able to use this deal
              after they purchase it. After it expires it will be valid for the
              paid value for 5 years or more according to the laws of your
              state.
            </FinePrint>
          </Header>
          <div
            style={{
              display: 'flex',
              justifyContent: 'between',
              alignItems: 'center'
            }}
          >
            <RoundButton
              onClick={this.decrease.bind(this, field('promoLength'))}
              disabled={field('promoLength').validations.val <= 1}
            >
              <i className="fa fa-minus" aria-hidden="true" />
              <span className="sr-only">Decrease</span>
            </RoundButton>
            <span
              style={{
                fontSize: '1.6rem',
                margin: '0 10px',
                minWidth: '122px',
                textAlign: 'center'
              }}
            >
              {field('promoLength').validations.val} Months
            </span>
            <RoundButton
              onClick={this.increase.bind(this, field('promoLength'))}
            >
              <i className="fa fa-plus" aria-hidden="true" />
              <span className="sr-only">Increase</span>
            </RoundButton>
          </div>
        </Step>
        <Step step="07">
          <Header>
            <Title>ADD PHOTO</Title>
          </Header>
          <div className="image-container">
            {croppedImage && (
              <img className="img-fluid" src={croppedImage} alt="post" />
            )}
            <div className="d-flex justify-content-between my-2 align-items-center">
              <ReactFileReader base64={true} handleFiles={handleImage}>
                <div className="btn-pill d-inline-flex align-items-center image-select px-3 py-1">
                  <i className="image-select-icon fa fa-camera mr-1" />
                  <span>{croppedImage ? 'Replace' : 'Add'} Photo</span>
                </div>
              </ReactFileReader>
              {croppedImage && (
                <div className="pull-right text-danger" onClick={removeImage}>
                  Remove Photo
                </div>
              )}
            </div>
          </div>
        </Step>
        <Step step="08">
          <label>
            <input
              style={{ marginRight: '10px' }}
              type="checkbox"
              onChange={this.toggleCustomCodes}
            />
           Use existing codes instead of Localvore voucher codes
          </label>
          {this.state.customCodes && (
            <TextAreaWrapper
              {...this.props}
              input={field('codes')}
              editing={true}
              placeholder="Add custom codes here, separated by commas" />
          )}
          <p>
            <small>
              <i className="fa fa-info-circle" aria-hidden="true" /> This is
              an optional step, Localvore.co handles codes generation for
              you.
            </small>
          </p>
        </Step>
        <Step step="09">
          <label>
            <CheckboxWrapper
              {...this.props}
              value={false}
              style={{ marginRight: '10px' }}
              input={field('terms')}
            />
            I have read and agree to{' '}
            <a
              href="https://pages.localvore.co/merchant-deal-terms-conditions"
              rel="noopener noreferrer"
              target="_blank"
            >
              Localvore's Terms & Conditions
            </a>
          </label>
        </Step>
        {/* <section className="mb-4">
        <h1 className="font-size-09 mb-2">FREQUENCY</h1>
        <button className="frequency-button btn-pill active px-3 py-1 mr-4">
          <i className="fa fa-retweet" aria-hidden="true" /> Repeating
        </button>
        <button className="frequency-button btn-pill px-3 py-1">
          <i className="fa fa-star-o" aria-hidden="true" /> One-Time
        </button>
      </section> */}
      </div>
    );
  }
}

DealPost.defaultProps = {
  charLimit: 200
};

DealPost.propTypes = {
  charLimit: PropTypes.number,
  counter: PropTypes.func.isRequired,
  field: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  valid: PropTypes.func.isRequired,
  croppedImage: PropTypes.array,
  handleImage: PropTypes.func.isRequired,
  removeImage: PropTypes.func.isRequired
};

Step.propTypes = {
  step: PropTypes.string.isRequired
};



// WEBPACK FOOTER //
// ./src/components/Social/DealPost.js